export const ID_CARD_CONFIG_PREFIX = '__ID_CARD_CONFIG__';

export const ID_CARD_CATEGORIES = [
  { key: 'staff', label: 'Staff ID Card' },
  { key: 'student', label: 'Student ID Card' },
];

export const ID_CARD_ITEM_TYPES = [
  {
    key: 'full_service',
    label: 'Full ID Card Service',
    labels: {
      staff: 'Full Staff ID Card Service',
      student: 'Full Student ID Card Service',
    },
  },
  { key: 'tag', label: 'Tag' },
  { key: 'card', label: 'Card' },
  { key: 'rfidcard', label: 'RFID Card' },
  { key: 'cardholder', label: 'Card Holder' },
  { key: 'additional_tag', label: 'Additional Tag' },
  { key: 'additional_card', label: 'Additional Card' },
  { key: 'additional_rfidcard', label: 'Additional RFID Card' },
  { key: 'additional_cardholder', label: 'Additional Card Holder' },
];

export const getIdCardItemLabel = (item, categoryKey) =>
  item?.labels?.[categoryKey] || item?.label || '';

const emptyItems = () =>
  ID_CARD_ITEM_TYPES.reduce((acc, item) => {
    acc[item.key] = { selected: false, count: '', unitPrice: '' };
    return acc;
  }, {});

export const createEmptyIdCardConfig = () => ({
  version: 1,
  categories: {
    staff: { enabled: false, items: emptyItems() },
    student: { enabled: false, items: emptyItems() },
  },
});

export const parseIdCardConfigFromNotes = (notes) => {
  if (!notes || typeof notes !== 'string') return null;
  if (!notes.startsWith(ID_CARD_CONFIG_PREFIX)) return null;
  try {
    const parsed = JSON.parse(notes.slice(ID_CARD_CONFIG_PREFIX.length));
    if (!parsed?.categories) return null;
    return normalizeIdCardConfig(parsed);
  } catch {
    return null;
  }
};

export const serializeIdCardConfigToNotes = (config) =>
  `${ID_CARD_CONFIG_PREFIX}${JSON.stringify(normalizeIdCardConfig(config))}`;

export const normalizeIdCardConfig = (config) => {
  const base = createEmptyIdCardConfig();
  ID_CARD_CATEGORIES.forEach(({ key }) => {
    const incoming = config?.categories?.[key] || {};
    base.categories[key].enabled = Boolean(incoming.enabled);
    ID_CARD_ITEM_TYPES.forEach(({ key: itemKey }) => {
      const item = incoming.items?.[itemKey] || {};
      base.categories[key].items[itemKey] = {
        selected: Boolean(item.selected),
        count: item.count ?? '',
        unitPrice: item.unitPrice ?? '',
      };
    });
  });
  return base;
};

export const calculateIdCardLineTotal = (item) => {
  if (!item?.selected) return 0;
  const count = parseFloat(item.count) || 0;
  const unitPrice = parseFloat(item.unitPrice) || 0;
  if (count <= 0 || unitPrice < 0) return 0;
  return count * unitPrice;
};

export const calculateIdCardConfigTotal = (config) => {
  if (!config?.categories) return 0;
  let total = 0;
  ID_CARD_CATEGORIES.forEach(({ key }) => {
    const category = config.categories[key];
    if (!category?.enabled) return;
    ID_CARD_ITEM_TYPES.forEach(({ key: itemKey }) => {
      total += calculateIdCardLineTotal(category.items?.[itemKey]);
    });
  });
  return total;
};

/** Flat list of selected ID card lines for issue-invoice checkboxes. */
export const listIdCardConfigLineItems = (config) => {
  if (!config?.categories) return [];
  const lines = [];
  ID_CARD_CATEGORIES.forEach(({ key: categoryKey, label: categoryLabel }) => {
    const category = config.categories[categoryKey];
    if (!category?.enabled) return;
    ID_CARD_ITEM_TYPES.forEach((itemType) => {
      const item = category.items?.[itemType.key];
      if (!item?.selected) return;
      const amount = calculateIdCardLineTotal(item);
      if (amount <= 0) return;
      const itemLabel = getIdCardItemLabel(itemType, categoryKey);
      lines.push({
        key: `${categoryKey}:${itemType.key}`,
        label: `${categoryLabel} — ${itemLabel}`,
        amount: Number(amount.toFixed(2)),
        count: parseFloat(item.count) || 0,
        unitPrice: parseFloat(item.unitPrice) || 0,
      });
    });
  });
  return lines;
};

export const calculateIdCardConfigTotalUnits = (config) => {
  if (!config?.categories) return 0;
  let units = 0;
  ID_CARD_CATEGORIES.forEach(({ key }) => {
    const category = config.categories[key];
    if (!category?.enabled) return;
    ID_CARD_ITEM_TYPES.forEach(({ key: itemKey }) => {
      const item = category.items?.[itemKey];
      if (!item?.selected) return;
      units += parseFloat(item.count) || 0;
    });
  });
  return units;
};

export const validateIdCardConfig = (config) => {
  const errors = [];
  if (!config?.categories) {
    return { valid: false, errors: ['Configure at least one ID card category.'] };
  }

  const enabledCategories = ID_CARD_CATEGORIES.filter(({ key }) => config.categories[key]?.enabled);
  if (!enabledCategories.length) {
    errors.push('Select Staff ID Card and/or Student ID Card.');
  }

  let hasSelectedItem = false;
  enabledCategories.forEach(({ key, label }) => {
    const category = config.categories[key];
    const selectedItems = ID_CARD_ITEM_TYPES.filter(({ key: itemKey }) => category.items?.[itemKey]?.selected);
    if (!selectedItems.length) {
      errors.push(`${label}: select Full ID Card Service and/or at least one item (tag, card, RFID, etc.).`);
      return;
    }
    selectedItems.forEach((itemType) => {
      hasSelectedItem = true;
      const itemLabel = getIdCardItemLabel(itemType, key);
      const item = category.items[itemType.key];
      const count = parseFloat(item.count);
      const unitPrice = parseFloat(item.unitPrice);
      if (!count || count <= 0) {
        errors.push(`${label} — ${itemLabel}: enter a valid count.`);
      }
      if (item.unitPrice === '' || item.unitPrice == null || Number.isNaN(unitPrice) || unitPrice < 0) {
        errors.push(`${label} — ${itemLabel}: enter a valid per unit price.`);
      }
    });
  });

  const total = calculateIdCardConfigTotal(config);
  if (hasSelectedItem && total <= 0) {
    errors.push('Total amount must be greater than zero.');
  }

  return { valid: errors.length === 0, errors };
};

export const formatIdCardCurrency = (value) => {
  const n = parseFloat(value) || 0;
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
